import { createAsyncThunk } from "@reduxjs/toolkit"

import { SCROLL_PAGE_SIZE } from "../../../../redux/shared/paged-list.ts"
import { type BasePagination } from "../../../../types/api.ts"
import { getLeads } from "../../../Leads/api/services.ts"
import { type LeadListItem } from "../../../Leads/types/types.ts"
import { getProjects } from "../../../Projects/api/services.ts"
import { type ProjectListItem } from "../../../Projects/types/types.ts"
import { getCustomerDetails } from "../../api/services.ts"
import { type Customer } from "../../types/customer.types.ts"

interface CustomerRelatedParams {
  customer: string
  page?: number
  /** Tells the reducer to append rather than replace; not a query parameter. */
  isMore?: boolean
}

interface CustomerRelatedPage<T> {
  results: T[]
  count: number
  next: string | null
}

export const fetchCustomerDetail = createAsyncThunk<Customer, string>(
  "customerDetail/fetchCustomer",
  async (id, { rejectWithValue }) => {
    const response = await getCustomerDetails(id)

    if (!response.ok || !response.data) {
      return rejectWithValue({ status: response.status })
    }

    return response.data
  }
)

export const fetchCustomerLeads = createAsyncThunk<
  CustomerRelatedPage<LeadListItem>,
  CustomerRelatedParams
>(
  "customerDetail/fetchLeads",
  async ({ customer, page = 1 }, { rejectWithValue }) => {
    const response = await getLeads({
      customer,
      page,
      page_size: SCROLL_PAGE_SIZE
    })

    if (!response.ok) return rejectWithValue(response.data)

    const data = response.data as BasePagination<LeadListItem> | undefined

    return {
      results: data?.results ?? [],
      count: data?.count ?? 0,
      next: data?.next ?? null
    }
  }
)

export const fetchCustomerProjects = createAsyncThunk<
  CustomerRelatedPage<ProjectListItem>,
  CustomerRelatedParams
>(
  "customerDetail/fetchProjects",
  async ({ customer, page = 1 }, { rejectWithValue }) => {
    const response = await getProjects({
      customer,
      page,
      page_size: SCROLL_PAGE_SIZE
    })

    if (!response.ok) return rejectWithValue(response.data)

    return {
      results: response.data?.results ?? [],
      count: response.data?.count ?? 0,
      next: response.data?.next ?? null
    }
  }
)

export { SCROLL_PAGE_SIZE, type CustomerRelatedPage }
